// The fact-find explainer library, EXP-01 to EXP-33 (SEP-243 section 8).
//
// The firm's reviewer wrote an education layer for the client journey and put
// it in the master specification. Section 8 of SEP-243 records that the copy
// never reached the platform. This module is that copy, and it is the ONE
// place the copy lives.
//
// # The copy is the firm's, and no author rewrites it
//
// The specification states that every explainer is "working draft for a retail
// audience in Consumer Duty tone - compliance sign-off required before release
// (D-15)". A financial-promotions review reads these exact words. An author
// who improves a sentence here breaks that review, because the reviewed text
// and the shown text then differ. Change a body ONLY when the firm sends new
// copy, and raise `FACT_FIND_EXPLAINER_LIBRARY_VERSION` when it does.
//
// # What "verbatim" means here
//
// The source is a Word document that reached us as extracted text, and the
// extraction left artefacts inside words. An artefact is not the firm's
// wording, so this module repairs the artefact and NOTHING else. Every repair
// is listed, so a reviewer can check each one against the source:
//
//   EXP-02  "''A standard"            -> '"A standard'      (a doubled apostrophe for an opening quote)
//   EXP-02  "''A tax<SHY> efficient"  -> '"A tax-efficient' (a soft hyphen and a stray space)
//   EXP-03  "That's fine -your"       -> "That's fine - your"        (a lost space)
//   EXP-04  "short<SHY> term"         -> "short-term"                (a soft hyphen and a stray space)
//   EXP-04  "pursuit oflong-term"     -> "pursuit of long-term"      (a lost space)
//   EXP-08  "higher<SHY> risk"        -> "higher-risk"               (a soft hyphen and a stray space)
//   EXP-12  "kind ofrole<SHY> "       -> "kind of role - "           (a lost space and a soft hyphen)
//   EXP-13  "protections -we're"      -> "protections - we're"       (a lost space)
//   EXP-15  "volatility- thesize"     -> "volatility - the size"     (two lost spaces)
//   EXP-18  "long<SHY> term"          -> "long-term"                 (a soft hyphen and a stray space)
//   EXP-23  no repair                 (listed so the reader knows it was checked)
//   EXP-29  "case<SHY> sensitive"     -> "case-sensitive"            (a soft hyphen and a stray space)
//   EXP-32  "W-SBEN"                  -> "W-8BEN"                    (an S read for an 8; the
//                                        specification's own field table writes W-8BEN)
//
// # EXP-33a to EXP-33f hold the firm's risk framework
//
// The specification named the risk-level descriptions and printed none of them,
// because they live in the firm's own risk framework. The firm supplied that
// copy on SEP-243 on 2026-08-27, and it is quoted here unchanged. It describes
// SIX profiles: the five risk levels and AIM IHT.
//
// This module is the ONE place the firm's risk wording lives.
// `RISK_PROFILE_TEMPLATES` carries the short form the mandate letter merges
// into the firm's Word template, and a test holds every sentence of that short
// form to a sentence of the body here, so no abridgement can paraphrase the
// firm.
//
// # How a question reaches its explainer
//
// `FACT_FIND_QUESTION_EXPLAINERS` binds a platform question id to an explainer
// id. `FACT_FIND_SECTION_EXPLAINERS` does the same for a whole section, because
// four explainers are section introductions and not question notes. The web
// form and the read-only review both read these maps, so one binding drives
// every surface.

/**
 * The version of the source library. It names the document the copy came from.
 * The `sep318` suffix records firm decision 5 of SEP-318 (15 October 2026),
 * which changes the equity wording of EXP-33b.
 */
import type { RiskProfile } from './risk-profile';

export const FACT_FIND_EXPLAINER_LIBRARY_VERSION = 'masterspec-2026-07-16-sep318';

/** Where the journey shows one explainer, in the specification's own words. */
export type FactFindExplainerPlacement =
    | 'inline'
    | 'inline_always_visible'
    | 'conditional'
    | 'helper_text'
    | 'interstitial'
    | 'under_question'
    | 'section_intro'
    // An introduction to a GROUP of questions inside a section (SEP-257 item
    // 10). It is bound to the FIRST question of the group and it reads once,
    // above that question. EXP-08 introduces the six product-experience rows,
    // and the platform holds no section of its own for them.
    | 'group_intro';

/**
 * One risk level of EXP-33.
 *
 * The master specification named these and printed no body, because the words
 * live in the firm's risk framework. The firm supplied them on SEP-243 on
 * 2026-08-27, and they are quoted here unchanged. There are SIX, not the five
 * the specification numbered: the firm's framework describes AIM IHT beside the
 * five risk levels, and `RISK_PROFILE_VALUES` has always held six.
 */
export interface FactFindExplainerSubEntry {
    readonly id: string;
    /**
     * The risk profile the body describes. It is a value of
     * `RISK_PROFILE_VALUES`, so a caller can look the copy up by the profile a
     * client chose rather than by the specification's letter.
     */
    readonly riskProfile: RiskProfile;
    /** The firm's copy, unchanged. */
    readonly body: string;
}

/** One versioned explainer. `body` is the firm's copy, unchanged. */
export interface FactFindExplainer {
    /** The specification's own id, `EXP-01` to `EXP-33`. */
    readonly id: string;
    /** The short name the specification gives the explainer. */
    readonly title: string;
    readonly placement: FactFindExplainerPlacement;
    /** The firm's copy. Never rewrite it. */
    readonly body: string;
    /** What the explainer is about, for a reviewer who reads the library alone. */
    readonly appliesTo: string;
    /** The six risk levels of EXP-33, and an empty list everywhere else. */
    readonly subEntries: readonly FactFindExplainerSubEntry[];
}

const EXP_33_SUB_ENTRIES: readonly FactFindExplainerSubEntry[] = [
    {
        id: 'EXP-33a',
        riskProfile: 'low',
        body: `You prioritise strong capital protection and seek high security for your assets, while accepting minimal levels of risk to achieve modest, consistent growth. You understand that returns will be closely tied to prevailing interest rates and the performance of fixed-income markets.

Your portfolio will primarily consist of cash deposits and up to 100% in high-quality fixed income assets, focusing on government debt, predominantly with short-term durations.

This strategy aims to deliver relatively stable returns while minimising the potential for capital loss. During periods of very low interest rates, portfolio returns may be constrained by management costs relative to available yields.`,
    },
    {
        id: 'EXP-33b',
        riskProfile: 'moderately_low',
        body: `You aim to achieve returns higher than cash deposits but lower than major equity markets, accepting occasional moderate capital losses as part of this strategy. You understand that volatility will be managed through a significant allocation to high-quality fixed-income assets.

Your portfolio may include:

- Up to 30% in equities, through funds only
- Up to 100% in fixed-income assets
- Up to 40% in diversified alternative investments
- Up to 40% in secure cash deposits
- This profile can hold up to 20% in Moderate Risk Securities
- This profile can hold up to 5% in Moderately-High Risk Securities

The actual mix of investments will be actively managed within these parameters, focusing on balancing return potential with risk control. This approach typically delivers more modest returns than equity-focused strategies but with lower volatility and reduced potential for significant losses. Equity exposure is through funds only. The portfolio does not hold individual company shares directly.

While the specific asset allocation may vary, the overall objective remains consistent: to provide steady returns while limiting downside risk through diversification across multiple asset classes, focusing on a balance of income and growth as the primary objective.`,
    },
    {
        id: 'EXP-33c',
        riskProfile: 'moderate',
        body: `You are prepared to accept short-term losses in pursuit of higher long-term returns, with substantial exposure to equity markets. You understand that during periods of extreme market stress, this strategy could still see significant losses, and such losses may take several years to recover.

Your portfolio may include:

- Up to 70% in equities
- Up to 40% in fixed income assets
- Up to 40% in diversified alternative investments
- Up to 25% in cash deposits
- This profile can hold up to 20% in Moderately High Risk Securities
- This profile can hold up to 15% in High Risk Securities

The strategy typically maintains a significant equity allocation as the primary driver of returns, while using other asset classes for diversification. Returns are likely to show meaningful correlation with major equity markets, though with some dampening of volatility through diversification.

This approach targets returns that move closer to those available from major equity markets, while maintaining some flexibility to adjust positioning based on market conditions and opportunities. The higher equity allocation means your portfolio may experience substantial short-term fluctuations in value. This risk profile is typically associated with Income, Growth and Balanced objectives.`,
    },
    {
        id: 'EXP-33d',
        riskProfile: 'moderately_high',
        body: `You have a high tolerance for risk and seek returns that closely track or exceed major equity markets over the long term. You fully accept that this approach comes with the potential for significant losses during periods of market stress, which may take several years to recover.

Your portfolio may include:

- Up to 100% in equities
- Up to 20% in fixed income assets
- Up to 20% in diversified alternative investments
- Up to 25% in cash deposits
- This profile can hold up to 40% in High Risk Securities

This strategy maintains high equity exposure as the primary driver of returns, with a significant allocation permitted to smaller companies which can increase both return potential and risk. The portfolio will typically experience similar or higher volatility compared to major equity markets, particularly during periods of market stress. This strategy can also hold up to 3% in Private shares.

While some diversification may be employed through fixed income and alternatives, the dominant characteristic of this portfolio will be its high equity exposure. You understand that this approach prioritises long-term capital growth and can lead to substantial fluctuations in portfolio value over shorter periods.`,
    },
    {
        id: 'EXP-33e',
        riskProfile: 'high',
        body: `You seek to maximise long-term returns through a high-growth investment strategy and have a very high tolerance for investment risk with the objective of capital growth. You understand and accept that the value of your investments may fluctuate significantly and that this approach may lead to substantial losses, particularly during periods of market volatility.

This strategy maintains a high allocation to equities and may include investments in higher-risk securities, including companies listed on AIM. The portfolio may also include up to 10% in private companies. The remainder of the portfolio may include up to 10% in fixed income assets, up to 10% in diversified alternative investments, and up to 30% in cash where appropriate.

This unconstrained mandate allows your Investment Manager to have the flexibility to identify opportunities across markets and adjust portfolio positioning when appropriate. Investments may include smaller companies and positions that differ materially from benchmark allocations.

Due to the focus on equities and smaller companies, your portfolio may experience greater price volatility than broader equity markets. You understand that losses may occur and that recovery periods may be extended. Portfolio performance may differ significantly from mainstream equity markets in both positive and negative market conditions.

This approach is intended for investors with a long-term investment horizon who understand the risks associated with higher-risk assets and smaller company investing. You accept that the portfolio may underperform broader markets for periods of time, particularly during market stress when smaller company shares may become less liquid.

As a result of the flexibility within this mandate, portfolio performance may differ materially from the stated benchmark. In certain market environments, this strategy may result in significant capital losses.`,
    },
    {
        id: 'EXP-33f',
        riskProfile: 'aim_iht',
        body: `This portfolio is designed for investors seeking Inheritance Tax planning benefits through AIM market investments, while accepting the significant risks associated with smaller company investing. You understand that this is a specialized investment strategy where tax planning objectives are balanced against notably high investment risks.

The strategy focuses exclusively on qualifying AIM-listed companies that meet Business Relief criteria. Your Investment Manager will maintain a portfolio of typically 20-30 AIM companies, selected for both their IHT-qualifying status and investment potential. While diversification will be sought where possible, you understand that the restricted investment universe of qualifying AIM companies means your portfolio will be relatively concentrated.

Your Investment Manager will seek companies with established business models, strong balance sheets, and sustainable competitive advantages. However, you fully understand that even well-established AIM companies carry inherently high risks. The portfolio will typically be fully invested in AIM shares to maintain IHT qualification, though may hold small cash positions (usually under 5%) for operational purposes.

You accept that AIM shares typically experience significantly higher volatility than mainstream market investments. These companies often have lower trading volumes, which can result in substantial price movements when buying or selling positions. During market stress, some holdings may become extremely illiquid, potentially preventing trading and leading to amplified price movements.

This approach requires a minimum two-year holding period to qualify for IHT relief, though a longer investment horizon is recommended. You understand that while the strategy targets both inheritance tax efficiency and capital growth, there may be extended periods of significant underperformance and capital loss. The focused nature of AIM investing means your portfolio value could fall substantially more than mainstream markets during periods of stress.

You acknowledge that maintaining IHT qualification may sometimes conflict with optimal investment decisions, as the manager may need to hold positions during adverse market conditions to preserve tax benefits. Performance can vary significantly from both AIM benchmarks and mainstream markets, particularly given the selective nature of IHT-qualifying companies.

The success of this strategy depends on both current IHT legislation remaining unchanged and companies maintaining their qualifying status. You understand that changes to either could impact the tax efficiency of your portfolio, potentially requiring repositioning that could result in substantial costs or losses.`,
    },
];

/** Every explainer of the library, in the specification's order. */
export const FACT_FIND_EXPLAINERS: readonly FactFindExplainer[] = [
    {
        id: 'EXP-01',
        title: 'Service type',
        placement: 'inline_always_visible',
        appliesTo: 'the choice of service type, Discretionary Managed or Advisory',
        subEntries: [],
        body:
            'Discretionary Managed: "We make and carry out investment decisions for you, within ' +
            "the objectives and risk profile we agree together. You won't be asked to approve " +
            "each transaction, but you'll receive regular reports and we'll contact you about " +
            'anything significant." Advisory: "We give you personal recommendations, but every ' +
            'decision is yours - nothing happens to your portfolio without your instruction. ' +
            'This works best if you want to stay closely involved and are comfortable making ' +
            'the final call, including acting promptly on time-sensitive recommendations."',
    },
    {
        id: 'EXP-02',
        title: 'Account type',
        placement: 'inline',
        appliesTo: 'the choice of account type, General Investment Account or ISA',
        subEntries: [],
        body:
            'General Investment Account: "A standard investment account with no special tax ' +
            "treatment. You may need to pay tax on income and gains - we'll report the " +
            'information you need, but we don\'t provide tax advice." ISA: "A tax-efficient ' +
            'account for UK residents. Income and gains within an ISA are free of UK income and ' +
            "capital gains tax. There's an annual limit on how much you can pay in across all " +
            "your ISAs (current allowance shown here). If you choose an ISA we'll ask a few " +
            'extra questions."',
    },
    {
        id: 'EXP-03',
        title: 'ISA already subscribed',
        placement: 'conditional',
        appliesTo: 'the question about an existing ISA subscription this tax year',
        subEntries: [],
        body:
            '"Based on your answer, you may have used some or all of this year\'s ISA allowance ' +
            "elsewhere. That's fine - your investment manager will discuss options with you, " +
            "such as using next year's allowance or moving existing investments into an ISA " +
            "over time (sometimes called 'Bed & ISA'). This may have tax implications, and we " +
            'recommend you take tax advice - Dowgate Wealth is not a tax adviser."',
    },
    {
        id: 'EXP-04',
        title: 'Investment objective',
        placement: 'inline',
        appliesTo: 'the choice of investment objective, Income, Growth or Balance',
        subEntries: [],
        body:
            'Income: "Your priority is regular payments from your portfolio - for example to ' +
            'supplement pension income. Portfolios built for income tend to hold more ' +
            'dividend-paying shares and bonds; the capital value will still rise and fall." ' +
            'Growth: "Your priority is increasing the value of your portfolio over time, and ' +
            "you don't need it to pay you a regular income. Growth portfolios accept more " +
            'short-term movement in pursuit of long-term gains." Balance: "You want a mix - ' +
            'some regular income and some long-term growth. This is a middle path between the ' +
            'two."',
    },
    {
        id: 'EXP-05',
        title: 'Restrictions',
        placement: 'helper_text',
        appliesTo: 'the free-text field for portfolio restrictions',
        subEntries: [],
        body:
            '"Tell us about anything you don\'t want to hold - for example tobacco or ' +
            'fossil-fuel companies, specific countries, or an individual company (perhaps one ' +
            'you work for). The more specific you are, the more precisely we can follow your ' +
            'wishes. Restrictions can narrow the investments available to us, which may affect ' +
            'performance versus an unrestricted portfolio."',
    },
    {
        id: 'EXP-06',
        title: 'Portfolio tenure',
        placement: 'under_question',
        appliesTo: 'the question about how long the client has held an investment portfolio',
        subEntries: [],
        body:
            '"We mean an account where money is invested in markets - with us, another manager, ' +
            "or self-managed. Cash savings accounts don't count; a workplace pension counts " +
            'only if you\'ve actively chosen its investments."',
    },
    {
        id: 'EXP-07',
        title: 'Knowledgeable investor definition',
        placement: 'inline',
        appliesTo:
            'the question asking if the client considers themselves a knowledgeable investor',
        subEntries: [],
        body:
            '"We consider a knowledgeable investor to be someone who understands why markets ' +
            'move the way they do - for example, how interest rates and inflation affect ' +
            'different types of investment. Be honest here: there are no wrong answers, and it ' +
            'simply helps us communicate with you in the right way."',
    },
    {
        id: 'EXP-08',
        title: 'Product experience grid',
        placement: 'group_intro',
        appliesTo: 'the product-experience grid, one row for each product type',
        subEntries: [],
        // SEP-257 item 10. The specification wrote this entry as a BUILD
        // instruction: one header, then six tooltips, in one paragraph. The
        // platform showed that paragraph under every one of the six rows, so a
        // client read the words "Header:" and "Tooltips:" six times. The body
        // now holds the header sentence alone, which is the only part a client
        // reads at the top of the grid. The six tooltips are not lost: they are
        // `PRODUCT_EXPERIENCE_TOOLTIPS` below, and each row shows its own one.
        // No word of the firm's copy changes.
        body:
            '"Which of the following have you had experience dealing with - or could ' +
            "confidently explain to a friend? Tell us how long you've been familiar with each. " +
            "If you've only heard of something but couldn't say how it works, choose 'None' - " +
            'that\'s completely fine."',
    },
    {
        id: 'EXP-09',
        title: 'AIM',
        placement: 'under_question',
        appliesTo: 'the question about past AIM investment experience',
        subEntries: [],
        body:
            '"AIM is the London Stock Exchange\'s market for smaller, growing companies. AIM ' +
            'shares can offer higher growth potential, but they are usually harder to buy and ' +
            'sell quickly and carry a higher risk of large losses than shares in bigger ' +
            'companies. We ask because smaller companies can feature in our portfolios, and ' +
            'your familiarity with them helps us assess what\'s suitable for you."',
    },
    {
        id: 'EXP-10',
        title: 'Placing / new issue',
        placement: 'under_question',
        appliesTo: 'the question about experience with placings or new issues',
        subEntries: [],
        body:
            "\"A 'placing' or 'new issue' is when a company raises money by selling new shares " +
            'to investors, often before those shares trade on the open market. Taking part - ' +
            'directly or through a fund - involves particular risks, such as limited information ' +
            'and the possibility the shares fall once trading begins. We ask because it tells us ' +
            'about your experience with this kind of opportunity."',
    },
    {
        id: 'EXP-11',
        title: 'Financial qualifications',
        placement: 'under_question',
        appliesTo: "the question about the client's financial or investment qualifications",
        subEntries: [],
        body:
            '"For example accountancy qualifications, or investment qualifications recognised by ' +
            'the FCA (such as CISI, IMC or CFA exams). This contributes to our picture of your ' +
            'investment knowledge - it doesn\'t change the service you receive."',
    },
    {
        id: 'EXP-12',
        title: 'Worked in the financial industry',
        placement: 'under_question',
        appliesTo: 'the question about whether the client has worked in the financial industry',
        subEntries: [],
        body:
            '"If yes, we\'ll ask what kind of role - experience in investment roles tells us ' +
            'something different from, say, operational roles. This helps us judge how much ' +
            'detail and explanation you\'d like from us."',
    },
    {
        id: 'EXP-13',
        title: 'Client classification',
        placement: 'interstitial',
        appliesTo: 'the retail-versus-professional client classification decision',
        subEntries: [],
        body:
            '"By default we treat you as a retail client. This gives you the highest level of ' +
            'protection under UK financial regulation - including detailed suitability ' +
            'obligations, clearer disclosures, and access to the Financial Ombudsman Service and ' +
            'the Financial Services Compensation Scheme where eligible. Some clients with ' +
            'substantial knowledge, experience and resources can ask to be treated as a ' +
            "professional client. If you're re-classified, you lose some of these protections - " +
            "we're allowed to assume you understand risks with less explanation, some " +
            'disclosures are reduced, and your access to the Ombudsman and FSCS may be affected. ' +
            'Most of our individual clients are retail clients, and being retail does not ' +
            'restrict the quality of service you receive. If you believe you qualify as a ' +
            'professional client and want to discuss it, tick here and your investment manager ' +
            "will contact you - we'll need to assess your experience and gather evidence before " +
            'any change is made."',
    },
    {
        id: 'EXP-14',
        title: 'Risk tolerance question rationale: protection versus returns',
        placement: 'under_question',
        appliesTo: 'the risk-tolerance question about protection versus returns',
        subEntries: [],
        body:
            '"This tells us where you sit on the fundamental trade-off: higher potential returns ' +
            'always come with a higher chance of losses."',
    },
    {
        id: 'EXP-15',
        title: 'Risk tolerance question rationale: fewer ups and downs',
        placement: 'under_question',
        appliesTo: 'the risk-tolerance question about wanting fewer ups and downs',
        subEntries: [],
        body:
            '"This gauges how you feel about volatility - the size of the rises and falls your ' +
            'portfolio may experience along the way, separate from where it ends up."',
    },
    {
        id: 'EXP-16',
        title: 'Risk tolerance question rationale: smaller companies comfort',
        placement: 'under_question',
        appliesTo: 'the risk-tolerance question about comfort with smaller companies',
        subEntries: [],
        body:
            '"Smaller companies can grow faster than large ones, but their shares can be harder ' +
            'to sell and individual companies are more likely to fail. This asks whether that ' +
            'trade-off appeals to you."',
    },
    {
        id: 'EXP-17',
        title: 'Risk tolerance question rationale: understanding smaller-company risk',
        placement: 'under_question',
        appliesTo: 'the risk-tolerance question about understanding smaller-company risk',
        subEntries: [],
        body:
            '"This is about understanding rather than appetite. If you\'re unsure what those ' +
            'risks are, our Risk & Investment Guide explains them - link here."',
    },
    {
        id: 'EXP-18',
        title: 'Risk tolerance question rationale: FTSE 2020 scenario',
        placement: 'under_question',
        appliesTo: 'the risk-tolerance scenario question about the FTSE fall in 2020',
        subEntries: [],
        body:
            '"There\'s no right answer. How people actually behave in sharp falls matters more ' +
            'to long-term outcomes than almost anything else, so we ask you to imagine it ' +
            'honestly."',
    },
    {
        id: 'EXP-19',
        title: 'Capacity question rationale: loss-cut point',
        placement: 'under_question',
        appliesTo: 'the capacity question about the loss level that would cause real concern',
        subEntries: [],
        body:
            '"This helps us understand at what point falls would cause you real concern - so the ' +
            'risk level we apply doesn\'t take you past it in normal market conditions."',
    },
    {
        id: 'EXP-20',
        title: 'Capacity question rationale: time horizon',
        placement: 'under_question',
        appliesTo: 'the capacity question about investment time horizon',
        subEntries: [],
        body:
            '"The longer you can stay invested, the more short-term movement a portfolio can ' +
            'ride out. If you expect to need the money within a couple of years, that changes ' +
            'what\'s suitable."',
    },
    {
        id: 'EXP-21',
        title: 'Capacity question rationale: income security, income change, liabilities change',
        placement: 'under_question',
        appliesTo:
            'the capacity questions about income security, expected income change and expected liability change',
        subEntries: [],
        body:
            '"If your income is secure and your commitments stable, you can generally afford ' +
            'more investment risk than if either might change. We look at these answers together ' +
            'with your income and outgoings."',
    },
    {
        id: 'EXP-22',
        title: 'Capacity question rationale: emergency funds',
        placement: 'under_question',
        appliesTo: 'the capacity question about emergency funds',
        subEntries: [],
        body:
            '"Money you could reach quickly, without selling investments - typically cash ' +
            "savings. Having a cash buffer means you're less likely to be forced to sell " +
            'investments at a bad time."',
    },
    {
        id: 'EXP-23',
        title: 'Capacity question rationale: acceptable loss without impact',
        placement: 'under_question',
        appliesTo:
            'the capacity question about the loss the client could absorb without an effect on their standard of living',
        subEntries: [],
        body:
            '"This is about capacity rather than feelings: as a matter of fact, how much of this ' +
            'investment could fall in value before your standard of living was affected? We ' +
            'compare this with the financial details you\'ve given us."',
    },
    {
        id: 'EXP-24',
        title: 'Financials intro',
        placement: 'section_intro',
        appliesTo: 'the introduction to the income, expenditure and assets section',
        subEntries: [],
        body:
            '"Please include income and assets held anywhere - not just what you plan to invest ' +
            'with us. A complete picture is a regulatory requirement and directly affects what ' +
            'we can responsibly recommend. Estimates are fine where exact figures are hard."',
    },
    {
        id: 'EXP-25',
        title: 'Income versus expenditure confirmation',
        placement: 'under_question',
        appliesTo: 'the free-text confirmation of income versus expenditure',
        subEntries: [],
        body: '"A simple sense-check in your own words - we\'ll compare it with the figures above."',
    },
    {
        id: 'EXP-26',
        title: 'Source of wealth and funds',
        placement: 'section_intro',
        appliesTo: 'the source of wealth and source of funds questions',
        subEntries: [],
        body:
            "\"'Source of wealth' is how you built your money over time; 'source of funds' is " +
            "where the specific money you'll invest is coming from. UK anti-money-laundering " +
            'rules require us to understand and, in some cases, evidence both. Specific detail ' +
            '(employer names, dates, sale details) speeds up your application."',
    },
    {
        id: 'EXP-27',
        title: 'Bank account',
        placement: 'under_question',
        appliesTo: 'the nominated bank account field',
        subEntries: [],
        body:
            '"For your protection, we only pay money to - and accept money from - this one ' +
            "nominated account in your own name. We'll need a certified copy of a bank " +
            'statement to verify it."',
    },
    {
        id: 'EXP-28',
        title: 'Third party disclosure',
        placement: 'under_question',
        appliesTo: 'the third-party disclosure field',
        subEntries: [],
        body:
            '"This lets us send copies of reports to, say, your accountant or lawyer. It\'s ' +
            'information only - a third party listed here cannot give us instructions or make ' +
            'decisions on your account."',
    },
    {
        id: 'EXP-29',
        title: 'Security questions',
        placement: 'under_question',
        appliesTo: 'the portal security-question setup',
        subEntries: [],
        body:
            "\"Choose four questions and give answers you'll remember exactly. We'll use these " +
            'to verify your identity for the online portal. Answers aren\'t case-sensitive."',
    },
    {
        id: 'EXP-30',
        title: 'Electronic communications consent',
        placement: 'under_question',
        appliesTo: 'the electronic communications consent question',
        subEntries: [],
        body:
            '"Regulation requires your express consent before we can send documents ' +
            "electronically instead of on paper. Without it we're unable to open the account. " +
            'You can still request specific documents in another durable format at any time."',
    },
    {
        id: 'EXP-31',
        title: 'Health section intro',
        placement: 'section_intro',
        appliesTo: 'the introduction to the health and vulnerability section',
        subEntries: [],
        body:
            '"These questions help us look after you properly - for example, adapting how we ' +
            "communicate or planning around upcoming events. Share only what you're comfortable " +
            'sharing; your answers are handled confidentially and never treated as a problem ' +
            'with your application."',
    },
    {
        id: 'EXP-32',
        title: 'US Person and W-forms',
        placement: 'under_question',
        appliesTo: 'the US Person status question and the associated W-form guidance',
        subEntries: [],
        body:
            "\"A 'US Person' broadly includes US citizens (including dual citizens), US tax " +
            "residents and green-card holders. US tax rules require specific forms: we'll guide " +
            'you through the right one (W-9 for US Persons; W-8BEN for others receiving ' +
            'US-source income) as part of this application."',
    },
    {
        id: 'EXP-33',
        title: 'Intended risk profile',
        placement: 'inline',
        appliesTo: "the client's selection of an intended risk profile level",
        subEntries: EXP_33_SUB_ENTRIES,
        body:
            '"This is your preference, not a decision - your investment manager will assess all ' +
            'your answers together and agree the final risk profile with you."',
    },
];

const BY_ID: ReadonlyMap<string, FactFindExplainer> = new Map(
    FACT_FIND_EXPLAINERS.map((explainer) => [explainer.id, explainer]),
);

/** One explainer by its specification id, or undefined for an unknown id. */
export function factFindExplainer(id: string): FactFindExplainer | undefined {
    return BY_ID.get(id);
}

// ---------------------------------------------------------------------------
// The bindings: which platform question or section shows which explainer
// ---------------------------------------------------------------------------

/**
 * One binding: a fact-find question id, and the explainer the platform shows
 * beside it.
 *
 * The bindings are a LIST of pairs and not an object literal. A question id is
 * snake_case, and the repository holds one rule about that: a snake_case string
 * is a VALUE and never a key. The id here is domain data, so it sits where a
 * value sits.
 */
export type FactFindExplainerBinding = readonly [questionId: string, explainerId: string];

/**
 * The explainer that each fact-find QUESTION shows.
 *
 * A question id is frozen, because it keys both the stored response row and the
 * AcroForm field of the firm's paper form. This list therefore never renames a
 * question; it only names the explainer beside it.
 *
 * An explainer with no entry here has no platform question yet.
 * The reconciliation comment on SEP-243 classifies every one of those.
 */
export const FACT_FIND_QUESTION_EXPLAINERS: readonly FactFindExplainerBinding[] = [
    // Knowledge and experience
    ['investment_objective', 'EXP-04'],
    ['experience_length', 'EXP-06'],
    ['knowledgeable_investor', 'EXP-07'],
    // EXP-08 introduces the whole product-experience grid, so it is bound to
    // the FIRST row alone and it reads once above the six (SEP-257 item 10).
    // Each row shows its own plain-English definition from
    // `PRODUCT_EXPERIENCE_TOOLTIPS`, and never the introduction again.
    ['exp_cash_deposits', 'EXP-08'],
    ['invested_in_aim', 'EXP-09'],
    ['placing_new_issue', 'EXP-10'],
    ['financial_qualifications', 'EXP-11'],
    ['worked_in_finance', 'EXP-12'],
    ['client_classification', 'EXP-13'],
    // The twelve-question risk instrument
    ['atr_q1', 'EXP-14'],
    ['atr_q2', 'EXP-15'],
    ['atr_q3', 'EXP-16'],
    ['atr_q4', 'EXP-17'],
    ['atr_q5', 'EXP-18'],
    ['atr_q6', 'EXP-19'],
    ['atr_q7', 'EXP-20'],
    ['atr_q8', 'EXP-21'],
    ['atr_q9', 'EXP-21'],
    ['atr_q10', 'EXP-21'],
    ['atr_q11', 'EXP-22'],
    ['atr_q12', 'EXP-23'],
    // Personal, health and tax
    ['general_health', 'EXP-31'],
    ['us_person', 'EXP-32'],
    // Restrictions
    ['restriction_categories', 'EXP-05'],
    ['restrictions_detail', 'EXP-05'],
];

/** The explainer that each fact-find SECTION shows above its questions. */
export const FACT_FIND_SECTION_EXPLAINERS: readonly FactFindExplainerBinding[] = [
    ['financial_situation', 'EXP-24'],
    ['source_of_wealth', 'EXP-26'],
];

const EXPLAINER_BY_QUESTION = new Map(FACT_FIND_QUESTION_EXPLAINERS);
const EXPLAINER_BY_SECTION = new Map(FACT_FIND_SECTION_EXPLAINERS);

/** The explainer bound to one question id, or undefined when none is bound. */
export function explainerForQuestion(questionId: string): FactFindExplainer | undefined {
    const id = EXPLAINER_BY_QUESTION.get(questionId);
    return id ? BY_ID.get(id) : undefined;
}

/** The explainer bound to one section id, or undefined when none is bound. */
export function explainerForSection(sectionId: string): FactFindExplainer | undefined {
    const id = EXPLAINER_BY_SECTION.get(sectionId);
    return id ? BY_ID.get(id) : undefined;
}

/**
 * The per-option copy of EXP-08, one entry for each product row of the grid.
 * The specification wrote these as tooltips inside the EXP-08 body; this list
 * quotes the same words, so a row shows its own definition and nothing else
 * (SEP-257 item 10). The row shows it behind its "What does this mean?"
 * toggle, which is where the specification asks for the definition.
 */
export const PRODUCT_EXPERIENCE_TOOLTIPS: readonly FactFindExplainerBinding[] = [
    ['exp_cash_deposits', 'Savings accounts and cash held for interest.'],
    ['exp_fixed_income', 'Bonds: loans to governments or companies that pay interest.'],
    ['exp_equities', 'Shares in individual companies.'],
    ['exp_funds', 'Pooled investments holding many assets, such as unit trusts, OEICs and ETFs.'],
    [
        'exp_alternatives',
        'Investments outside shares, bonds and cash - for example property funds, commodities or private assets.',
    ],
    [
        'exp_derivatives',
        'Contracts whose value depends on something else, such as options and futures; typically higher-risk and more complex.',
    ],
];

const TOOLTIP_BY_QUESTION = new Map(PRODUCT_EXPERIENCE_TOOLTIPS);

/** The one-line tooltip of one product-experience row, where EXP-08 writes one. */
export function productExperienceTooltip(questionId: string): string | undefined {
    return TOOLTIP_BY_QUESTION.get(questionId);
}

/**
 * The per-option copy of EXP-04, keyed by the OPTION LABEL of the fact-find
 * `investment_objective` question. The labels are frozen: the PDF filler ticks
 * the option box at the label's index, so a renamed label fills the wrong box.
 */
/** The three fact-find option labels EXP-04 writes an explanation for. */
export type InvestmentObjectiveOptionLabel = 'Income' | 'Growth' | 'Balance of Income and Growth';

export const INVESTMENT_OBJECTIVE_EXPLANATIONS: Readonly<
    Record<InvestmentObjectiveOptionLabel, string>
> = {
    Income:
        'Your priority is regular payments from your portfolio - for example to supplement ' +
        'pension income. Portfolios built for income tend to hold more dividend-paying shares ' +
        'and bonds; the capital value will still rise and fall.',
    Growth:
        'Your priority is increasing the value of your portfolio over time, and you ' +
        "don't need it to pay you a regular income. Growth portfolios accept more short-term " +
        'movement in pursuit of long-term gains.',
    'Balance of Income and Growth':
        'You want a mix - some regular income and some long-term growth. This is a middle path ' +
        'between the two.',
};

/**
 * The firm's risk-framework copy for one risk profile (EXP-33a to EXP-33f), or
 * null for a profile the framework does not describe.
 *
 * The intended-risk control of the enquiry and the risk-profile recommendation
 * of the Internal Review both read it, so a client and a reviewer see the same
 * words.
 */
export function riskProfileExplainer(profile: RiskProfile | null | undefined): string | null {
    if (!profile) return null;
    const exp33 = factFindExplainer('EXP-33');
    return exp33?.subEntries.find((entry) => entry.riskProfile === profile)?.body ?? null;
}
